import {
  Box,
  Button,
  Center,
  HStack,
  Icon,
  Image,
  Link,
  Spacer,
  Text,
  VStack,
} from '@chakra-ui/react';
import { getAssetUrl } from '@cronos-app/chakra';
import { CollapsedText, NoSSR, useIsDesktop } from '@cronos-app/components';
import { ChevronRight, IconLinkOff, Link as LinkIcon } from '@cronos-app/icons';
import { getChainOfLayer } from '@cronos-app/sdk';
import { currentWallet } from '@cronos-app/wallet';
import { useTxGuardFn } from '@ui/hooks/useTxGuardFn';
import { LINK_MAP } from '@ui/views/card-cronos/core/configs';
import { CardView } from '@ui/views/card-cronos/core/types';
import { useCardLayer } from '@ui/views/card-cronos/core/useCardContextStore';
import {
  useCdcCardView,
  useCdcDepositAddress,
  useTopUpHistoryDepositAddresses,
} from '@ui/views/card-cronos/core/utils';
import { useTranslations } from 'next-intl';

import { useCardBindingStore } from './CardBinding';
import { FAQs } from './shared/FAQs';
import { HomeDivider } from './shared/HomeDivider';
import { SharedHeader } from './shared/SharedHeader';
import { TopUpHistory } from './TopUpAndHistory';

export const CdcCardFirstView = () => {
  const t = useTranslations();
  const { data } = useTopUpHistoryDepositAddresses();
  return (
    <Box
      pb={{
        base: '12',
        desktop: '6',
      }}
    >
      <SharedHeader />
      <VStack px="5">
        <Text
          bg="gradients.white-blue-title"
          bgClip="text"
          textAlign="center"
          textStyle="h3"
          mb="3"
        >
          {t('card.From Crypto to Everyday Spending')}
        </Text>
        <Text textAlign="center" textStyle="body2">
          {t(
            'card.Link your Crypto-com card to your wallet and use your funds anywhere you shop',
          )}
        </Text>
        <Link href={LINK_MAP.howDoesItWork()} mb="6">
          <HStack color="#159DFF" spacing="4px">
            <Text textStyle="body2Bold">{t('card.How does it work?')}</Text>
            <Icon as={LinkIcon} boxSize="3" />
          </HStack>
        </Link>
        <Steps />
        <VStack
          mt="3"
          rounded="8px"
          gap="2.5"
          w="full"
          p="2"
          border="1px solid"
          borderColor="brand.gray-scale-border-20"
          bg="rgba(255, 255, 255, 0.05)"
          alignItems="flex-start"
          color="brand.gray-scale-light-grey"
          pos="relative"
        >
          <Text textStyle="body2">{t('card.Got funds on Cronos zkEVM?')}</Text>
          <Link href={LINK_MAP.cronosZkevmTopUp()} textStyle="cta">
            <HStack color="#159DFF" spacing="4px">
              <Text>{t('card.Top Up on Cronos zkEVM')}</Text>
              <Icon as={LinkIcon} boxSize="3" />
            </HStack>
          </Link>
          <Image
            pos="absolute"
            top="0"
            right="0"
            zIndex="-1"
            h="72px"
            src={getAssetUrl('/images/cdc-bind-card/cronos-zkevm-topup.png')}
            alt="CDC App"
          />
        </VStack>
        <FAQs />
        {!!data?.length && (
          <Box mt="40px" mx="5" w="full">
            <Text
              textAlign="center"
              textStyle="body1Bold"
              color="brand.secondary-sky-blue"
              mb="4"
            >
              {t('card.Top Up History')}
            </Text>
            <HomeDivider mb="6" />
            <TopUpHistory />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

const Steps = () => {
  const t = useTranslations();
  const { data: card } = useCdcDepositAddress();
  const network = currentWallet.useDesiredNetwork();
  const walletConfig = currentWallet.useWalletConfig();
  const account = currentWallet.useAccount();
  const isConnected = currentWallet.useIsConnected();
  const allSet = isConnected && !!card;
  const layer = useCardLayer();
  const connect = useTxGuardFn(getChainOfLayer(network, layer));
  const { setView } = useCdcCardView();
  const isMobile = !useIsDesktop();
  return (
    <Box rounded="8px" bg="gradients.sky-blue-20" w="full" px="3">
      <HomeDivider mb="3" />
      <Text
        textStyle="subtitle3"
        bg="gradients.white-blue-title"
        bgClip="text"
        mb="3"
        w="fit-content"
      >
        {allSet
          ? t('card.All set to top up!')
          : t('card.Follow steps to top up funds')}
      </Text>
      <Box textStyle="body2" color="brand.gray-scale-light-grey">
        <Text mb="2">
          {allSet
            ? t('card.Onchain wallet')
            : '1. ' + t('card.Connect onchain wallet')}
        </Text>
        {isConnected ? (
          <HStack gap="1">
            <Image src={walletConfig.icon} boxSize="6" alt="wallet icon" />
            <CollapsedText
              w={{
                base: '100px',
                desktop: '120px',
              }}
              remain={4}
              text={account ?? ''}
              textStyle="body1Bold"
            />
            <Icon
              cursor="pointer"
              as={IconLinkOff}
              boxSize="5"
              onClick={() => {
                currentWallet.disconnect();
              }}
            />
          </HStack>
        ) : (
          <Button
            variant="ghost"
            bgColor="#159DFF"
            rounded="4px"
            size="sm"
            w="full"
            onClick={connect}
            textTransform="unset"
          >
            {t('card.Connect')}
          </Button>
        )}
      </Box>
      <HomeDivider my="3" />
      <Box textStyle="body2" color="brand.gray-scale-light-grey">
        <Text mb="2">
          {allSet
            ? t('card.Crypto-com wallet')
            : '2. ' + t('card.Link Crypto-com wallet')}
        </Text>
        {card ? (
          <HStack gap="1">
            <Image
              src={getAssetUrl('/images/cdc-app.svg')}
              boxSize="6"
              alt="CDC App"
            />
            <CollapsedText
              w={{
                base: '100px',
                desktop: '120px',
              }}
              remain={4}
              text={card}
              textStyle="body1Bold"
            />
            <Icon
              cursor="pointer"
              as={IconLinkOff}
              boxSize="5"
              onClick={() => {
                useCardBindingStore.getState().open(false);
              }}
            />
            <Spacer />
            <Button
              variant="ghost"
              bgColor="#159DFF"
              rounded="4px"
              size="sm"
              onClick={() => {
                setView(CardView.Second);
              }}
              textTransform="unset"
            >
              <HStack gap="0">
                <Text>{t('card.Top Up')}</Text>
                <Icon as={ChevronRight} boxSize="5" />
              </HStack>
            </Button>
          </HStack>
        ) : (
          <>
            <Button
              variant="ghost"
              bgColor="#159DFF"
              rounded="4px"
              size="sm"
              mb="2"
              w="full"
              onClick={() => {
                useCardBindingStore.getState().open(true);
              }}
              textTransform="unset"
              isDisabled={!isConnected}
            >
              {t('card.Link Crypto-com wallet')}
            </Button>
            <Center>
              <NoSSR>
                <Link href={LINK_MAP.getCard(isMobile)} isExternal>
                  <HStack color="#159DFF" spacing="4px">
                    <Text textStyle="body2Bold">
                      {t('card.Get Crypto-com Card')}
                    </Text>
                    <Icon as={LinkIcon} boxSize="3" />
                  </HStack>
                </Link>
              </NoSSR>
            </Center>
          </>
        )}
      </Box>
      <HomeDivider mt="3" />
    </Box>
  );
};
